import React, { useMemo } from 'react';
import { Filter, Square, CheckSquare, Maximize2, BookOpen, CheckCircle, XCircle } from 'lucide-react';
import { EditableFmeaRow } from '../../types';
import { FmeaStandardConfig as StandardConfig } from '../../constants/fmeaStandards';
import { FilterableColumn } from '../../utils/fmeaTableUtils';
import { FilterDropdown } from './FilterDropdown';
import { LayoutSettings } from './LayoutSettingsPanel';
import SmartCell from '../../components/SmartCell';

// Numeric-only columns — auto-narrow
const NUMERIC_COLS = new Set(['S', 'O', 'D', 'S_rating', 'O_rating', 'D_rating']);
const NUMERIC_COL_WIDTH = 52;

// Task-side columns that should NEVER wrap (dates, responsibility, IDs)
const NO_WRAP_COLS = new Set([
    'actionResponsibility', 'actionTargetDate', 'actionCompletionDate',
    'id', 'specialCharacteristic', 'actionPriority',
]);

interface FmeaEditorTableProps {
    data: EditableFmeaRow[];
    standardConfig: StandardConfig;
    selectedRowIds: Set<string>;
    toggleRowSelection: (id: string) => void;
    toggleSelectAll: () => void;
    handleSort: (key: string) => void;
    sortConfig: { key: string | null; direction: 'asc' | 'desc' | null };
    columnWidths: Record<string, number>;
    handleMouseDown: (key: string) => void;
    columnFilters: Record<FilterableColumn, Set<string>>;
    toggleFilterDropdown: (column: FilterableColumn) => void;
    activeFilterDropdown: FilterableColumn | null;
    handleSelectAllColumnFilter: (column: FilterableColumn) => void;
    handleColumnFilterChange: (column: FilterableColumn, value: string) => void;
    onCloseFilterDropdown: () => void;
    uniqueOptions: Record<string, string[]>;
    layoutSettings: LayoutSettings;
    openHistoryModal: (item: EditableFmeaRow) => void;
    handleCellChange: (index: number, field: any, value: any) => void;
    openControlModal: (index: number, type: 'prevention' | 'detection', value: string) => void;
    pendingChanges: Record<string, any>;
    openCommentModal: (index: number, field: any) => void;
    handleCancelChange: (index: number, field: any) => void;
    openCellPopout: (index: number, field: any) => void;
    getMetricColor: (val: any, isAP?: boolean) => string;
    calculateActionPriority: (s: number, o: number, d: number) => string;
    getRiskLevel: (standard: string, s: number, o: number, d: number) => string;
    fmeaStandard: string;
}

export const FmeaEditorTable = React.forwardRef<HTMLTableElement, FmeaEditorTableProps>((props, ref) => {
    const {
        data, standardConfig, selectedRowIds, toggleRowSelection, toggleSelectAll,
        handleSort, sortConfig, columnWidths, handleMouseDown,
        columnFilters, toggleFilterDropdown, activeFilterDropdown,
        handleSelectAllColumnFilter, handleColumnFilterChange, onCloseFilterDropdown,
        uniqueOptions, layoutSettings, openHistoryModal, handleCellChange,
        openControlModal, pendingChanges, openCommentModal, handleCancelChange,
        openCellPopout, getMetricColor, calculateActionPriority, getRiskLevel, fmeaStandard
    } = props;

    const { wrapText, colorMode, showCellBorders, hiddenColumns, riskRanges } = layoutSettings;

    const visibleColumns = useMemo(() => {
        return standardConfig.columns.filter(c => !hiddenColumns.has(c.key));
    }, [standardConfig.columns, hiddenColumns]);

    const columnOrder = useMemo(() => visibleColumns.map(c => c.key), [visibleColumns]);
    const columnHeaders = useMemo(() => {
        const h: Record<string, string> = {};
        visibleColumns.forEach(c => { h[c.key] = c.header; });
        return h;
    }, [visibleColumns]);

    // --- Row coloring logic ---
    const getRowClass = (item: EditableFmeaRow, idx: number): string => {
        if (selectedRowIds.has(item.id)) return 'bg-primary/5';

        const S = Number(item.S) || 0;
        const O = Number(item.O) || 0;
        const D = Number(item.D) || 0;
        const maxRating = Math.max(S, O, D);

        if (colorMode === 'risk-traffic-light') {
            if (maxRating >= riskRanges.red.min && maxRating <= riskRanges.red.max)
                return 'bg-red-50 dark:bg-red-900/10 hover:bg-red-100/50';
            if (maxRating >= riskRanges.yellow.min && maxRating <= riskRanges.yellow.max)
                return 'bg-yellow-50 dark:bg-yellow-900/10 hover:bg-yellow-100/50';
            if (maxRating >= riskRanges.green.min && maxRating <= riskRanges.green.max)
                return 'bg-green-50 dark:bg-green-900/10 hover:bg-green-100/50';
            return 'hover:bg-slate-50/50 dark:hover:bg-slate-800/30';
        }
        if (colorMode === 'alt-gray')
            return idx % 2 === 0 ? 'bg-white dark:bg-slate-900 hover:bg-slate-50' : 'bg-slate-50 dark:bg-slate-800/40 hover:bg-slate-100/60';
        if (colorMode === 'alt-blue')
            return idx % 2 === 0 ? 'bg-white dark:bg-slate-900 hover:bg-blue-50/30' : 'bg-blue-50/50 dark:bg-blue-900/10 hover:bg-blue-100/40';
        if (colorMode === 'alt-green-white')
            return idx % 2 === 0 ? 'bg-white dark:bg-slate-900 hover:bg-emerald-50/30' : 'bg-emerald-50/40 dark:bg-emerald-900/10 hover:bg-emerald-100/40';

        return 'hover:bg-slate-50/50 dark:hover:bg-slate-800/30';
    };

    const borderClass = showCellBorders ? 'border border-border' : 'border-transparent';
    const tdBase = `px-2 py-2.5 relative group/cell ${showCellBorders ? 'border border-border/40' : ''}`;

    return (
        <>
            <table
                ref={ref}
                className={`w-full text-sm text-left ${wrapText ? 'table-auto' : 'table-fixed'}`}
                style={{ minWidth: '1400px', borderCollapse: showCellBorders ? 'collapse' : 'separate', borderSpacing: 0 }}
            >
                <thead className="bg-slate-50 dark:bg-slate-900/80 sticky top-0 z-40 backdrop-blur-md">
                    <tr className={`border-b-2 border-border shadow-sm ${showCellBorders ? 'border border-border' : ''}`}>
                        {/* Checkbox col */}
                        <th className="px-2 py-3 w-10 text-center border-r border-border/30 bg-slate-50 dark:bg-slate-900/80">
                            <button onClick={toggleSelectAll} className="text-primary hover:scale-110 transition-transform">
                                {selectedRowIds.size > 0 && selectedRowIds.size === data.length
                                    ? <CheckSquare size={16} /> : <Square size={16} />}
                            </button>
                        </th>
                        {columnOrder.map(key => {
                            const isNum = NUMERIC_COLS.has(key);
                            const colWidth = isNum ? NUMERIC_COL_WIDTH : (columnWidths[key] || standardConfig.columns.find(c => c.key === key)?.width || 180);
                            return (
                                <th
                                    key={key}
                                    data-col-key={key}
                                    className={`px-2 py-3 font-bold text-text-primary text-[11px] uppercase tracking-wider relative group cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${showCellBorders ? 'border border-border/40' : ''}`}
                                    style={{ width: colWidth, minWidth: isNum ? NUMERIC_COL_WIDTH : 80, maxWidth: isNum ? NUMERIC_COL_WIDTH : undefined }}
                                    onClick={() => handleSort(key)}
                                >
                                    <div className="flex items-center justify-between gap-1 overflow-hidden">
                                        <span className={isNum ? 'text-center w-full' : 'truncate'}>{columnHeaders[key]}</span>
                                        <div className="flex items-center gap-0.5 flex-shrink-0">
                                            {sortConfig.key === key && (
                                                <span className="text-primary text-[10px]">
                                                    {sortConfig.direction === 'asc' ? '▲' : '▼'}
                                                </span>
                                            )}
                                            {['id', 'function', 'itemFunction', 'specialCharacteristic'].includes(key) && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); toggleFilterDropdown(key as FilterableColumn); }}
                                                    className={`p-1 rounded-md transition-all ${columnFilters[key as FilterableColumn]?.size > 0 ? 'bg-primary/20 text-primary shadow-sm' : 'text-slate-400 opacity-0 group-hover:opacity-100'}`}
                                                >
                                                    <Filter size={10} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    {/* Col resizer (skip for numeric) */}
                                    {!isNum && (
                                        <div
                                            onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(key); }}
                                            className="w-1.5 h-full cursor-col-resize absolute right-0 top-0 hover:bg-primary/30 active:bg-primary/50 transition-colors z-10"
                                        />
                                    )}
                                    {activeFilterDropdown === key && (
                                        <FilterDropdown
                                            column={key as FilterableColumn}
                                            options={uniqueOptions[key] || []}
                                            selected={columnFilters[key as FilterableColumn]}
                                            onSelectAll={handleSelectAllColumnFilter}
                                            onFilterChange={handleColumnFilterChange}
                                            onClose={onCloseFilterDropdown}
                                        />
                                    )}
                                </th>
                            );
                        })}
                        {/* RPN calc cols */}
                        {standardConfig.riskModel === 'RPN' && !hiddenColumns.has('_rpn') && (
                            <>
                                <th className="px-2 py-3 text-center text-[11px] font-bold text-text-primary uppercase tracking-wider bg-slate-100/50 dark:bg-slate-800/50 w-14" onClick={() => handleSort('sxo')}>
                                    S×O {sortConfig.key === 'sxo' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                                </th>
                                <th className="px-2 py-3 text-center text-[11px] font-bold text-text-primary uppercase tracking-wider bg-slate-100/50 dark:bg-slate-800/50 w-14" onClick={() => handleSort('sxd')}>
                                    S×D {sortConfig.key === 'sxd' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                                </th>
                                <th className="px-2 py-3 text-center text-[11px] font-bold text-text-primary uppercase tracking-wider bg-slate-100/50 dark:bg-slate-800/50 w-16" onClick={() => handleSort('rpn')}>
                                    RPN {sortConfig.key === 'rpn' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                                </th>
                            </>
                        )}
                        <th className="w-6 px-1" />
                    </tr>
                </thead>
                <tbody>
                    {data.map((item, idx) => {
                        const S = Number(item.S) || 0;
                        const O = Number(item.O) || 0;
                        const D = Number(item.D) || 0;
                        const rpn = S * O * D;
                        const sxo = S * O;
                        const sxd = S * D;
                        const rowBg = getRowClass(item, idx);

                        return (
                            <tr key={item.id} className={`group border-b border-border/30 transition-colors ${rowBg}`}>
                                {/* Checkbox */}
                                <td className={`px-2 py-2.5 text-center border-r border-border/20 ${showCellBorders ? 'border border-border/40' : ''}`}>
                                    <button
                                        onClick={() => toggleRowSelection(item.id)}
                                        className={`transition-all ${selectedRowIds.has(item.id) ? 'text-primary' : 'text-slate-300 group-hover:text-slate-400'}`}
                                    >
                                        {selectedRowIds.has(item.id) ? <CheckSquare size={15} /> : <Square size={15} />}
                                    </button>
                                </td>

                                {columnOrder.map(key => {
                                    const isNumeric = NUMERIC_COLS.has(key);
                                    const isDropdown = ['function', 'itemFunction', 'requirement', 'failureMode', 'effects'].includes(key);
                                    const isPending = pendingChanges[item.id]?.[key] !== undefined;

                                    // ── ID cell ──
                                    if (key === 'id') {
                                        return (
                                            <td key={key} className={`px-3 py-2.5 font-medium ${showCellBorders ? 'border border-border/40' : ''} ${wrapText ? 'whitespace-normal break-all' : 'truncate max-w-[140px]'}`}>
                                                <button
                                                    onClick={() => openHistoryModal(item)}
                                                    className="text-blue-500 hover:text-blue-600 hover:underline font-mono text-xs tracking-tighter"
                                                    title={item.id}
                                                >
                                                    {item.id.replace('FMEA-NEW-', '🆕 ')}
                                                </button>
                                            </td>
                                        );
                                    }

                                    // ── Control cells ──
                                    if (key === 'prevControls' || key === 'detControls') {
                                        const cellWrap = wrapText && !NO_WRAP_COLS.has(key);
                                        return (
                                            <td key={key} className={tdBase}>
                                                <div className="relative flex items-center">
                                                    <SmartCell
                                                        value={item[key] as string}
                                                        wrapText={cellWrap}
                                                        isInput
                                                        onChange={v => handleCellChange(idx, key, v)}
                                                        className={isPending ? 'pl-6 bg-amber-50 dark:bg-amber-900/10 border-amber-200' : ''}
                                                    />
                                                    <button
                                                        onClick={() => openControlModal(idx, key === 'prevControls' ? 'prevention' : 'detection', item[key] as string)}
                                                        className="absolute right-1 top-1 text-slate-400 hover:text-primary opacity-0 group-hover/cell:opacity-100 transition-opacity"
                                                        title="Select from Library"
                                                    >
                                                        <BookOpen size={12} />
                                                    </button>
                                                </div>
                                                {isPending && (
                                                    <div className="absolute left-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5 bg-white shadow-md border border-amber-200 rounded-sm p-0.5 z-10">
                                                        <button onClick={() => openCommentModal(idx, key)} className="p-0.5 hover:bg-emerald-50 text-emerald-600"><CheckCircle size={10} /></button>
                                                        <button onClick={() => handleCancelChange(idx, key)} className="p-0.5 hover:bg-rose-50 text-rose-500"><XCircle size={10} /></button>
                                                    </div>
                                                )}
                                            </td>
                                        );
                                    }

                                    // ── Numeric cell (S, O, D) ──
                                    if (isNumeric) {
                                        const val = Number(item[key as keyof EditableFmeaRow]) || 0;
                                        const numBg = val >= riskRanges.red.min && val <= riskRanges.red.max
                                            ? 'bg-red-100 text-red-800 border-red-200'
                                            : val >= riskRanges.yellow.min && val <= riskRanges.yellow.max
                                                ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                                                : 'bg-emerald-50 text-emerald-800 border-emerald-200';
                                        return (
                                            <td key={key} className={`text-center ${showCellBorders ? 'border border-border/40' : ''} py-2`} style={{ width: NUMERIC_COL_WIDTH }}>
                                                <input
                                                    type="number"
                                                    value={val}
                                                    onChange={e => handleCellChange(idx, key, parseInt(e.target.value) || 0)}
                                                    min={1} max={10}
                                                    className={`w-9 h-8 rounded-md border text-center font-black text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors mx-auto block ${numBg}`}
                                                />
                                            </td>
                                        );
                                    }

                                    // ── Special Characteristic ──
                                    if (key === 'specialCharacteristic') {
                                        return (
                                            <td key={key} className={`px-1 py-2 ${showCellBorders ? 'border border-border/40' : ''}`}>
                                                <select
                                                    value={item.specialCharacteristic || 'none'}
                                                    onChange={e => handleCellChange(idx, 'specialCharacteristic', e.target.value)}
                                                    className={`w-full p-1 rounded border text-[11px] font-bold text-center cursor-pointer transition-colors ${item.specialCharacteristic === 'CC' ? 'bg-red-100 text-red-800 border-red-300' : item.specialCharacteristic === 'SC' ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-transparent border-transparent text-slate-400'}`}
                                                >
                                                    <option value="none">—</option>
                                                    <option value="CC">CC</option>
                                                    <option value="SC">SC</option>
                                                </select>
                                            </td>
                                        );
                                    }

                                    // ── Action Priority (read-only badge) ──
                                    if (key === 'actionPriority') {
                                        const ap = calculateActionPriority(item.S, item.O, item.D);
                                        return (
                                            <td key={key} className={`px-2 py-2 text-center ${showCellBorders ? 'border border-border/40' : ''}`}>
                                                <div className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-black text-sm ${getMetricColor(ap, true)}`}>{ap}</div>
                                            </td>
                                        );
                                    }

                                    // ── Text / Dropdown cells (with NO_WRAP_COLS exclusion) ──
                                    const cellWrap = wrapText && !NO_WRAP_COLS.has(key);
                                    return (
                                        <td key={key} className={tdBase}>
                                            {isDropdown ? (
                                                <>
                                                    <SmartCell
                                                        value={item[key as keyof EditableFmeaRow] as string}
                                                        wrapText={cellWrap}
                                                        isInput
                                                        onChange={v => handleCellChange(idx, key, v)}
                                                        className={isPending ? 'pl-6 bg-amber-50 dark:bg-amber-900/10 border-amber-200' : ''}
                                                    />
                                                    {!cellWrap && (
                                                        <datalist id={`dl-${item.id}-${key}`}>
                                                            {uniqueOptions[key]?.map(opt => <option key={opt} value={opt} />)}
                                                        </datalist>
                                                    )}
                                                </>
                                            ) : key.includes('Date') ? (
                                                // Date columns always compact
                                                <input
                                                    type="date"
                                                    value={item[key as keyof EditableFmeaRow] as string}
                                                    onChange={e => handleCellChange(idx, key, e.target.value)}
                                                    className={`w-full bg-transparent p-1.5 rounded-md border border-transparent group-hover/cell:border-border focus:border-primary focus:bg-background focus:ring-1 focus:ring-primary/30 outline-none text-xs transition-all ${isPending ? 'pl-6 bg-amber-50' : ''}`}
                                                />
                                            ) : (
                                                <SmartCell
                                                    value={item[key as keyof EditableFmeaRow] as string}
                                                    wrapText={cellWrap}
                                                    isInput
                                                    onChange={v => handleCellChange(idx, key, v)}
                                                    className={isPending ? 'pl-6 bg-amber-50 dark:bg-amber-900/10 border-amber-200' : ''}
                                                />
                                            )}

                                            {/* Expand button */}
                                            {!isNumeric && key !== 'actionPriority' && key !== 'specialCharacteristic' && (
                                                <button
                                                    onClick={() => openCellPopout(idx, key)}
                                                    className="absolute right-1 top-1 p-0.5 text-slate-400 hover:text-primary opacity-0 group-hover/cell:opacity-100 transition-opacity bg-background shadow-sm rounded border border-border"
                                                    title="Expand"
                                                >
                                                    <Maximize2 size={10} />
                                                </button>
                                            )}

                                            {isPending && (
                                                <div className="absolute left-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5 bg-white shadow-md border border-amber-200 rounded-sm p-0.5 z-10">
                                                    <button onClick={() => openCommentModal(idx, key)} className="p-0.5 hover:bg-emerald-50 text-emerald-600"><CheckCircle size={10} /></button>
                                                    <button onClick={() => handleCancelChange(idx, key)} className="p-0.5 hover:bg-rose-50 text-rose-500"><XCircle size={10} /></button>
                                                </div>
                                            )}
                                        </td>
                                    );
                                })}

                                {/* RPN computed cells */}
                                {standardConfig.riskModel === 'RPN' && !hiddenColumns.has('_rpn') && (
                                    <>
                                        <td className={`px-2 py-2 text-center font-bold text-sm ${getMetricColor(sxo)} ${showCellBorders ? 'border border-border/40' : ''}`}>{sxo}</td>
                                        <td className={`px-2 py-2 text-center font-bold text-sm ${getMetricColor(sxd)} ${showCellBorders ? 'border border-border/40' : ''}`}>{sxd}</td>
                                        <td className={`px-2 py-2 text-center font-black text-base ${showCellBorders ? 'border border-border/40' : ''}`}>{rpn}</td>
                                    </>
                                )}
                                <td className="w-6 px-1" />
                            </tr>
                        );
                    })}
                    {data.length === 0 && (
                        <tr>
                            <td colSpan={columnOrder.length + 4} className="text-center py-16 text-text-secondary italic">
                                No FMEA rows found. Add a row to get started.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </>
    );
});
FmeaEditorTable.displayName = 'FmeaEditorTable';
